/**
 * Administration → Storage → Migrations (1.1.0, administrators): managed mass moves and copies between storage
 * locations. The list (source → destination, a status line such as "Waiting for the window, opens Tue 23:00",
 * files and bytes progress, rate, ETA, failed), Pause and Resume, and New migration. ?id=<id> opens one migration;
 * ?new=1 (with &source=<location id> from a location card) opens the wizard, ?edit=<id> continues a draft.
 */
import { useSearchParams } from 'react-router-dom';
import { ArrowRightLeft, Pause, Play, Plus } from 'lucide-react';
import type { MigrationDTO, StorageLocationSummary } from '@prism/contracts';
import { errorDetail } from '../../../api/client';
import { useMigration, useMigrationMutations, useMigrations } from '../../../api/hooks';
import { toast } from '../../../components/Toasts';
import { relative, rate } from '../../../lib/format';
import { MigrationDetail } from './MigrationDetail';
import { MigrationBars } from './MigrationParts';
import { MigrationWizard } from './MigrationWizard';
import { MIGRATION_STATUS_LABEL, aheadLabel, statusLine, statusTone, windowOfMigration } from './migrationText';

export function Migrations({ locations }: { locations: StorageLocationSummary[] }) {
  const [params, setParams] = useSearchParams();
  const id = params.get('id');
  const editId = params.get('edit');
  const wizard = params.get('new') === '1';
  const go = (p: Record<string, string>) => setParams({ view: 'migrations', ...p });
  const editing = useMigration(editId);

  const wizardEl = wizard
    ? <MigrationWizard locations={locations} sourceId={params.get('source')} onClose={() => go({})} onStarted={(x) => go({ id: x })} />
    : editId && editing.data
      ? <MigrationWizard key={editId} locations={locations} initial={editing.data} onClose={() => go(id ? { id } : {})} onStarted={(x) => go({ id: x })} />
      : null;

  if (id) {
    return (
      <>
        <MigrationDetail id={id} onBack={() => go({})} onEdit={(m) => go({ id: m.id, edit: m.id })} />
        {wizardEl}
      </>
    );
  }
  return (
    <>
      <MigrationList onOpen={(m) => go({ id: m.id })} onNew={() => go({ new: '1' })} />
      {wizardEl}
    </>
  );
}

function MigrationList({ onOpen, onNew }: { onOpen: (m: MigrationDTO) => void; onNew: () => void }) {
  const q = useMigrations();
  const items = q.data?.items ?? [];
  const muts = useMigrationMutations();
  const now = Date.now();
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  };
  return (
    <div data-testid="migrations">
      <div className="mig-list-head">
        <h2 style={{ margin: 0 }}>Storage migrations</h2>
        <span style={{ flex: 1 }} />
        <button className="btn primary" onClick={onNew} data-testid="new-migration"><Plus size={14} /> New migration</button>
      </div>
      <p className="lede">Move or copy many files from one location to another: planned first as a dry run, then run inside a transfer window with limits, paused on errors, and reviewed before the sources are removed. System status alerts administrators when one needs attention.</p>
      {q.error ? <div className="banner error"><div className="banner-body"><span>{errorDetail(q.error)}</span></div></div>
        : !q.data ? <div className="skeleton" style={{ height: 160 }} />
          : items.length === 0 ? (
            <div className="empty" data-testid="migrations-empty"><div className="empty-inner">
              <div className="empty-icon"><ArrowRightLeft size={22} /></div>
              <h2>No migrations yet</h2>
              <p>Start one here, or with Migrate files… on a location’s card.</p>
              <button className="btn primary" onClick={onNew}><Plus size={14} /> New migration</button>
            </div></div>
          ) : (
            <div className="panel-card mig-list-card">
              <table className="table mig-table mig-list" data-testid="migrations-table">
                <thead><tr><th>Migration</th><th>State</th><th>Progress</th><th>Rate</th><th>Finishes</th><th>Failed</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.id} tabIndex={0} onClick={() => onOpen(m)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(m); }} data-testid="migration-row" data-name={m.name} data-status={m.status}>
                      <td><div className="mig-name">{m.name}</div><div className="faint mig-route">{m.source.name} → {m.destination.name} · {m.action === 'move' ? 'Move' : 'Copy'}</div></td>
                      <td className="mig-state-cell"><span className={`pill ${statusTone(m.status)}`}>{MIGRATION_STATUS_LABEL[m.status]}</span>
                        <div className="faint mig-line" data-testid="migration-status-line">{statusLine(m, windowOfMigration(m), now)}</div></td>
                      <td style={{ minWidth: 200 }}>{m.counters.total.files ? <MigrationBars m={m} compact /> : <span className="faint">—</span>}</td>
                      <td className="muted">{m.bytesPerSecond != null && m.status === 'running' ? rate(m.bytesPerSecond) : '—'}</td>
                      <td className="muted">{m.etaAt && (m.status === 'running' || m.status === 'paused') ? aheadLabel(m.etaAt, now) : m.finishedAt ? relative(m.finishedAt) : '—'}</td>
                      <td className="num" style={m.counters.failed.files ? { color: 'var(--danger)' } : undefined}>{m.counters.failed.files ? m.counters.failed.files.toLocaleString('en-US') : '—'}</td>
                      <td className="mig-row-actions" onClick={(e) => e.stopPropagation()}>
                        {m.status === 'running' && <button className="btn ghost sm" onClick={() => void act(() => muts.pause.mutateAsync({ id: m.id, body: { cancelRunning: false } }), `Paused ${m.name}`)} data-testid="migration-pause"><Pause size={13} /> Pause</button>}
                        {m.status === 'paused' && <button className="btn ghost sm" onClick={() => void act(() => muts.resume.mutateAsync({ id: m.id }), `Resumed ${m.name}`)} data-testid="migration-resume"><Play size={13} /> Resume</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );
}
